import Utils from '../../../common/util/Utils';
import {
    DetailedSolution,
    NIA_MEASURE_IMPACT_CLAZZ_VALUE,
    NiaMeasureImpact,
    ReroutedFlightDto,
} from '../../../model/SOL49';
import { DeltaTvImpact } from './DeltaTvImpact';
import { DeltaRegulationsPanel } from './DeltaRegulationsPanel';
import { DEFAULT_MAP_OPTIONS, MapPanel, MapPanelLayerOptions } from '../map/MapPanel';
import { Flex } from '../../common/Flex';
import memoizeOne from 'memoize-one';
import { SolutionMeasureImpact } from '../../dashboard/network/map/Map';
import TimePeriodUtils from '../../../common/util/TimePeriodUtils';
import { TimePeriod } from '../../../model/CORE';
import React from 'react';
import ExtensiblePanel, { ExtensiblePanelProps, ExtensiblePanelState } from '../../../common/panel/ExtensiblePanel';
import { ModalPanelProps } from '../DashboardModal';
import { setLoadConvection } from '../../../redux/features/dashboard/dashboard-slice';
import { cleanDetailedSolutions, setDetailedSolutions } from '../../../redux/features/solver/solution-slice';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../../../redux/store';

const mapStateToProps = (state: RootState, ownProps) => {
    return {
        mapConvectionRedux: state.dashboardData.convectionData, //just named with Redux suffix so we know where is coming from
    };
};

const mapDispatchToProps = {
    setLoadConvection,
    setDetailedSolutions,
    cleanDetailedSolutions,
};

export const solverConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof solverConnector>;

export interface AbstractSolverPanelProps extends ModalPanelProps, ExtensiblePanelProps, PropsFromRedux {}

export interface AbstractSolverPanelState extends ExtensiblePanelState {
    solverSelectedSolutionIdx: number;
    solverSolutions: Array<DetailedSolution>;
    solverSidePanelType: 'tvs' | 'measures' | 'map';
    solverSelectedRrtFlights: ReroutedFlightDto[];
}

export abstract class AbstractSolverPanel<
    P extends AbstractSolverPanelProps,
    S extends AbstractSolverPanelState,
> extends ExtensiblePanel<P, S> {
    static MAP_OPTIONS = Object.assign(DEFAULT_MAP_OPTIONS, { centerOnSelectedTvs: true });
    protected mapPanelRef = React.createRef<MapPanel>();

    protected abstract getMapLayerOptions(): MapPanelLayerOptions;

    protected constructor(props: P) {
        super(props);
        this.state = Object.assign(this.state, {
            solverSelectedRrtFlights: [],
        });
    }

    componentDidMount() {
        this.props.setLoadConvection(1);
    }

    componentWillUnmount() {
        this.props.setLoadConvection(-1);
        this.props.cleanDetailedSolutions();
    }

    handleShowOnMap = () => {
        if (this.state.solverSidePanelType !== 'map') {
            this.setState({ solverSidePanelType: 'map' }, () => this.showSidePanel(3));
        } else {
            this.setState({ solverSidePanelType: undefined }, () => this.hideSidePanel(3));
        }
    };

    handleRrtFlightShowOnMap = (flights: ReroutedFlightDto[]) => {
        this.setState({ solverSelectedRrtFlights: flights });
    };

    handleShowTVsImpact = () => {
        if (this.state.solverSidePanelType !== 'tvs') {
            this.setState({ solverSidePanelType: 'tvs' }, () => this.showSidePanel(3));
        } else {
            this.setState({ solverSidePanelType: undefined }, () => this.hideSidePanel(3));
        }
    };

    handleShowMeasuresImpact = () => {
        if (this.state.solverSidePanelType !== 'measures') {
            this.setState({ solverSidePanelType: 'measures' }, () => this.showSidePanel(3));
        } else {
            this.setState({ solverSidePanelType: undefined }, () => this.hideSidePanel(3));
        }
    };

    getTvImpactsFromSolution = memoizeOne((solution: DetailedSolution) => {
        return solution.solutionImpact?.tvImpact?.map(tvImpact => ({ ...tvImpact, solutionName: solution.name }));
    });

    getMeasureImpactsFromSolution = memoizeOne((solution: DetailedSolution): Array<SolutionMeasureImpact> => {
        const solutionMeasures = solution.measures.map(m => m.measureId.id);
        const output: Array<SolutionMeasureImpact> = [];
        for (const measureImpactsKey in solution.solutionImpact.measureImpacts) {
            const measure = solution.solutionImpact.measureImpacts[measureImpactsKey];
            if (
                measure.clazz === NIA_MEASURE_IMPACT_CLAZZ_VALUE &&
                !solutionMeasures.includes((measure as NiaMeasureImpact).regulId)
            ) {
                output.push(Object.assign({}, measure, { solutionName: solution.name }) as SolutionMeasureImpact);
            }
        }
        return output;
    });

    getSolutionTimePeriod = memoizeOne((solution: DetailedSolution) => {
        if (solution?.solutionImpact?.tvImpact?.length > 0) {
            // const timePeriod = {};
            // return solution.solutionImpact.tvImpact.forEach(tvImpact => {TimePeriodUtils.growToCover(timePeriod, tvImpact.timePeriod)});
            // return timePeriod;
            return solution?.solutionImpact?.tvImpact?.reduce((acc, tvImpact) => {
                TimePeriodUtils.growToCover(acc, tvImpact.timePeriod);
                return acc;
            }, {} as TimePeriod);
        }
        return null;
    });

    getSelectedFlightsForMap = memoizeOne((flights: Array<ReroutedFlightDto>) => {
        return flights.map(flight => flight.flight);
    });

    getSolverExtraPanelContent = () => {
        if (Utils.isNotExists(this.state.solverSelectedSolutionIdx)) return <></>;

        const solution = this.state.solverSolutions[this.state.solverSelectedSolutionIdx];
        let content = undefined;
        switch (this.state.solverSidePanelType) {
            case 'tvs':
                content = <DeltaTvImpact deltaTvImpacts={solution.solutionImpact?.deltaTvImpacts} />;
                break;
            case 'measures':
                const impactedDeltaRegulations = solution.solutionImpact?.deltaRegulations?.filter(
                    deltaRegul =>
                        deltaRegul.beforeDelayInMin !== deltaRegul.afterSituation.delayInMin ||
                        deltaRegul.beforeImpactedFlightCount !== deltaRegul.afterSituation.impactedFlightCount,
                );
                content = <DeltaRegulationsPanel deltaRegulations={impactedDeltaRegulations} />;
                break;
            case 'map':
                content = (
                    <MapPanel
                        ref={this.mapPanelRef}
                        mapConvection={this.props.mapConvectionRedux}
                        fullscreenLike={true}
                        flights={this.getSelectedFlightsForMap(this.state.solverSelectedRrtFlights)}
                        tvIds={solution.tvs}
                        mapOptions={AbstractSolverPanel.MAP_OPTIONS}
                        showVpd={true}
                        impactedTvs={this.getTvImpactsFromSolution(solution)}
                        impactedMeasures={this.getMeasureImpactsFromSolution(solution)}
                        layerOptions={this.getMapLayerOptions()}
                        timePeriod={this.getSolutionTimePeriod(solution)}
                    />
                );
                break;
        }
        if (content) {
            return (
                <Flex direction="vertical" style={{ borderLeft: '0.3em solid #191919', padding: '2em' }}>
                    <Flex direction="vertical">{content}</Flex>
                </Flex>
            );
        }
        return null;
    };
}
